import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AlertCircle, CheckCircle } from "lucide-react";

// Réplica do Toast original: fixo no topo, primary (sucesso) ou destructive
// (erro), radius 14, fade in/out, auto-esconde em 3s.
export type ToastData = { type: "success" | "error"; message: string };

type ToastContextValue = {
  showToast: (toast: ToastData) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [visible, setVisible] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const showToast = useCallback((next: ToastData) => {
    clearTimers();
    setToast(next);
    setVisible(true);
    timers.current.push(
      setTimeout(() => setVisible(false), 3000),
      setTimeout(() => setToast(null), 3250),
    );
  }, []);

  useEffect(() => clearTimers, []);

  const isSuccess = toast?.type === "success";

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast && (
        <div
          role="alert"
          className={`pointer-events-none fixed inset-x-5 top-4 z-50 mx-auto flex max-w-md items-center gap-2.5 rounded-(--radius-btn) px-4 py-3.5 shadow-lg shadow-black/25 transition-opacity duration-200 ${
            visible ? "opacity-100" : "opacity-0"
          } ${
            isSuccess
              ? "bg-primary text-primary-foreground"
              : "bg-destructive text-destructive-foreground"
          }`}
        >
          {isSuccess ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <p className="text-sm font-semibold">{toast.message}</p>
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast deve ser usado dentro de <ToastProvider>");
  return ctx;
}
